import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faPhone,
  faUserNurse,
  faWaveSquare,
  faSort,
  faSortUp,
  faSortDown,
  faUser,
  faChartLine,
  faClipboardList,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatTriageDetected,
  TRIAGE_BUCKET_LABEL,
  TRIAGE_BUCKET_TONE,
} from "../../utils/careTriageModel";
import TriageReviewDialogue, { isSupervisorUser } from "./TriageReviewDialogue";
import ClientAvatar from "../ClientAvatar";
import "./TriageQueueTable.css";

const WORKFLOW_SECTIONS = [
  { id: "new", label: "New", tone: "red" },
  { id: "assigned", label: "Assigned / In Progress", tone: "blue" },
  { id: "overdue", label: "Overdue", tone: "amber" },
];

function SourceBadge({ sourceType, label }) {
  const icons = {
    caregiver: faPhone,
    nurse: faUserNurse,
    device: faWaveSquare,
    sensi: faWaveSquare,
    aip: faUserNurse,
    review: faUserNurse,
    able: faWaveSquare,
  };
  const icon = icons[sourceType] || faUserNurse;
  return (
    <span className={`triage-source triage-source--${sourceType || "aip"}`} title={label}>
      <FontAwesomeIcon icon={icon} />
      <span className="triage-source__text">{label}</span>
    </span>
  );
}

function StatusPill({ workflowStatus }) {
  const map = {
    new: { label: "New", className: "new" },
    assigned: { label: "Assigned", className: "progress" },
    in_progress: { label: "In Progress", className: "progress" },
    overdue: { label: "Overdue", className: "overdue" },
    resolved: { label: "Resolved", className: "resolved" },
  };
  const item = map[workflowStatus] || map.new;
  return (
    <span className={`triage-status-pill triage-status-pill--${item.className}`}>
      {item.label}
    </span>
  );
}

function ReviewStatusCell({ row, sectionId }) {
  if (sectionId === "new") return "—";
  if (sectionId === "resolved") {
    return row.reviewResult || row.reviewStatusLabel || "Approved and Closed";
  }
  return row.reviewStatusLabel || "—";
}

function KV({ label, value }) {
  const empty = value == null || value === "" || value === "—";
  return (
    <div className="triage-kv__row">
      <dt>{label}</dt>
      <dd>{empty ? "—" : value}</dd>
    </div>
  );
}

function PriorityTag({ priority }) {
  return (
    <span className={`triage-priority triage-priority--${priority}`}>
      <span className="triage-priority__dot" />
      {priorityLabel(priority)}
    </span>
  );
}

function ReviewStatusPill({ row, sectionId }) {
  const label = ReviewStatusCell({ row, sectionId });
  if (!label || label === "—") return <span className="triage-muted">—</span>;
  const tone = /follow-up/i.test(label)
    ? "amber"
    : /ready/i.test(label) || /approved/i.test(label)
      ? "green"
      : "blue";
  return <span className={`triage-review-pill triage-review-pill--${tone}`}>{label}</span>;
}

function priorityLabel(priority) {
  if (priority === "high") return "High";
  if (priority === "low") return "Low";
  return "Normal";
}

function SortableHeader({ label, sortKey, sort, onSort }) {
  if (!sortKey || !onSort) return <span>{label}</span>;
  const active = sort?.key === sortKey;
  const icon = !active ? faSort : sort.dir === "asc" ? faSortUp : faSortDown;
  return (
    <button
      type="button"
      className={`triage-sort-header${active ? " is-active" : ""}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      <FontAwesomeIcon icon={icon} className="triage-sort-header__icon" />
    </button>
  );
}

function TriageGridRow({
  row,
  expanded,
  onToggle,
  onView,
  onAssign,
  sectionId,
  showBucketBadge,
  showReviewColumn,
  showResolvedColumns,
  currentUser,
  onSubmitMessage,
  onRequestClose,
  onRequestReopen,
  onExploreInterventions,
}) {
  const prev = row.aipPrevious ?? "—";
  const curr = row.aipCurrent ?? "—";
  const delta = row.change != null ? Math.abs(Number(row.change)) : null;
  const scoreDecline = Number(row.change) < 0;
  const ageLine = [
    row.age ? `${row.age} yrs` : "—",
    row.clientShortId || "",
  ]
    .filter(Boolean)
    .join(" - ");
  const bucketTone = TRIAGE_BUCKET_TONE[row.bucket] || "gray";
  const showAssignOnly =
    sectionId === "new" && (row.workflowStatus === "new" || !row.owner);
  const hideCollapsedActions = sectionId === "assigned" || sectionId === "overdue" || sectionId === "resolved";
  const hasDialogue = hideCollapsedActions;
  const supervisor = isSupervisorUser(currentUser);
  const hasUnread = supervisor ? row.unreadForSupervisor : row.unreadForAssignee;

  return (
    <>
      <div
        role="row"
        className={`triage-grid-row triage-row triage-row--${row.workflowStatus} triage-row--bucket-${bucketTone}${expanded ? " is-expanded" : ""}${showBucketBadge ? " triage-row--all-buckets" : ""}`}
        onClick={onToggle}
      >
        <div role="cell" className="triage-grid-cell triage-col-expand" aria-hidden="true">
          <FontAwesomeIcon
            icon={expanded ? faChevronDown : faChevronRight}
            className="triage-row-chevron"
          />
        </div>
        <div role="cell" className="triage-grid-cell triage-col-client">
          <div className="triage-client-cell">
            {showBucketBadge ? (
              <span
                className={`triage-bucket-tag triage-bucket-tag--${bucketTone}`}
                title={TRIAGE_BUCKET_LABEL[row.bucket]}
              >
                {row.bucket === "action"
                  ? "Red"
                  : row.bucket === "monitor"
                    ? "Yellow"
                    : row.bucket === "stable"
                      ? "Green"
                      : "Gray"}
              </span>
            ) : null}
            <ClientAvatar
              client={row}
              className="triage-avatar"
              initialClassName="triage-avatar-initial"
              size={36}
              alt={row.clientName}
            />
            <span className="triage-client-text">
              <strong>
                {row.clientName}
                {hasUnread && sectionId !== "resolved" ? (
                  <span className="triage-unread-pill" title="Unread review activity">
                    New
                  </span>
                ) : null}
              </strong>
              <span className="triage-client-sub">{ageLine}</span>
            </span>
          </div>
        </div>
        <div
          role="cell"
          className="triage-grid-cell triage-col-score triage-col-score--link"
          onClick={(e) => {
            e.stopPropagation();
            onView(row);
          }}
        >
          <div className={`triage-score-cell${scoreDecline ? " triage-score-cell--down" : ""}`}>
            <span className="triage-score-main">
              {prev} → {curr}
            </span>
            {delta != null && Number(row.change) < 0 ? (
              <span className="triage-score-delta">↓ {delta}</span>
            ) : null}
            {delta != null && Number(row.change) > 0 ? (
              <span className="triage-score-delta triage-score-delta--up">↑ {delta}</span>
            ) : null}
          </div>
        </div>
        <div role="cell" className="triage-grid-cell triage-col-changed" title={row.note || ""}>
          <span className="triage-changed-text">{row.note || "—"}</span>
        </div>
        <div role="cell" className="triage-grid-cell triage-col-source">
          <SourceBadge sourceType={row.sourceType} label={row.source} />
        </div>
        <div role="cell" className="triage-grid-cell triage-col-detected">
          {formatTriageDetected(row.changeDate)}
        </div>
        <div role="cell" className="triage-grid-cell triage-col-status">
          <StatusPill workflowStatus={row.workflowStatus} />
        </div>
        {showReviewColumn ? (
          <div role="cell" className="triage-grid-cell triage-col-review">
            <ReviewStatusPill row={row} sectionId={sectionId} />
          </div>
        ) : null}
        <div role="cell" className="triage-grid-cell triage-col-owner">
          {row.owner || "—"}
        </div>
        <div
          role="cell"
          className={`triage-grid-cell triage-col-due${row.workflowStatus === "overdue" ? " triage-due--late" : ""}`}
        >
          {row.dueDate ? formatTriageDetected(row.dueDate) : "—"}
        </div>
        <div role="cell" className="triage-grid-cell triage-col-priority">
          <span
            className={`triage-priority triage-priority--${row.priority}`}
            title={row.priority}
          >
            <span className="triage-priority__dot" />
            {priorityLabel(row.priority)}
          </span>
        </div>
        <div role="cell" className="triage-grid-cell triage-col-notes" title={row.notes || ""}>
          <span className="triage-changed-text">{row.notes || "—"}</span>
        </div>
        {showResolvedColumns ? (
          <>
            <div role="cell" className="triage-grid-cell triage-col-resolved-at">
              {row.resolvedAt ? formatTriageDetected(row.resolvedAt) : "—"}
            </div>
            <div role="cell" className="triage-grid-cell triage-col-resolved-by">
              {row.resolvedBy || "—"}
            </div>
            <div role="cell" className="triage-grid-cell triage-col-outcome" title={row.outcome || ""}>
              <span className="triage-changed-text">{row.outcome || "—"}</span>
            </div>
          </>
        ) : null}
        {!showResolvedColumns ? (
          <div
            role="cell"
            className="triage-grid-cell triage-col-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {!hideCollapsedActions ? (
              <div className="triage-actions-inline">
                {showAssignOnly ? (
                  <button
                    type="button"
                    className="triage-btn-assign"
                    onClick={() => onAssign?.(row)}
                  >
                    Assign
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {expanded ? (
        <div role="row" className="triage-grid-row triage-row-expand">
          <div role="cell" className="triage-grid-expand">
            <div className="triage-expand-grid">
              <section className="triage-expand-card">
                <h4 className="triage-expand-card__title">
                  <FontAwesomeIcon icon={faUser} /> Client Summary
                </h4>
                <dl className="triage-kv">
                  <KV label="Name" value={row.clientName} />
                  <KV label="Age" value={row.age ? `${row.age} years` : null} />
                  <KV label="Client ID" value={row.clientShortId} />
                  <KV label="Address" value={row.clientAddress} />
                  <KV
                    label="Phone"
                    value={
                      row.clientPhone ? (
                        <a href={`tel:${row.clientPhone}`} className="triage-kv__link">
                          <FontAwesomeIcon icon={faPhone} /> {row.clientPhone}
                        </a>
                      ) : null
                    }
                  />
                  <KV label="Care Manager" value={row.careManager} />
                  <KV label="Assigned RN" value={row.assignedRn || row.owner} />
                </dl>
              </section>

              <section className="triage-expand-card">
                <div className="triage-expand-card__head">
                  <h4 className="triage-expand-card__title">
                    <FontAwesomeIcon icon={faChartLine} />
                    {hasDialogue ? " AIP / Event / Assignment Details" : " AIP Score & Event Details"}
                  </h4>
                  <button type="button" className="triage-link-btn" onClick={() => onView(row)}>
                    View Full Aging in Place Score →
                  </button>
                </div>
                <div className="triage-expand-score">
                  <span className={`triage-expand-score__main${scoreDecline ? " is-down" : ""}`}>
                    {prev} → {curr}
                  </span>
                  {row.change != null && Number(row.change) !== 0 ? (
                    <span className={`triage-expand-score__delta${scoreDecline ? " is-down" : " is-up"}`}>
                      {scoreDecline ? "↓" : "↑"} {delta}
                    </span>
                  ) : null}
                </div>
                <dl className="triage-kv">
                  <KV label="What Changed" value={row.note} />
                  <KV
                    label="Source"
                    value={<SourceBadge sourceType={row.sourceType} label={row.source} />}
                  />
                  <KV label="Detected" value={formatTriageDetected(row.changeDate)} />
                  {hasDialogue ? (
                    <>
                      <KV label="Action Type" value={row.actionType} />
                      <KV label="Instructions" value={row.assignmentNotes} />
                      <KV
                        label="Due Date & Time"
                        value={
                          row.dueDate ? (
                            <span className={row.workflowStatus === "overdue" ? "triage-due--late" : ""}>
                              {formatTriageDetected(row.dueDate)}
                              {row.workflowStatus === "overdue" ? " (Overdue)" : ""}
                            </span>
                          ) : null
                        }
                      />
                      <KV label="Priority" value={<PriorityTag priority={row.priority} />} />
                    </>
                  ) : null}
                </dl>
                {onExploreInterventions && sectionId !== "resolved" ? (
                  <button
                    type="button"
                    className="triage-btn-view triage-btn-view--secondary"
                    onClick={() => onExploreInterventions(row)}
                  >
                    Explore Possible Interventions
                  </button>
                ) : null}
                {(row.auditTrail || []).length > 0 ? (
                  <details className="triage-audit-trail">
                    <summary>Event audit trail ({row.auditTrail.length})</summary>
                    <ul>
                      {row.auditTrail.map((a) => (
                        <li key={a._id || `${a.at}-${a.type}`}>
                          <strong>{formatTriageDetected(a.at)}</strong> — {a.type.replace(/_/g, " ")}
                          {a.userName ? ` (${a.userName})` : ""}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </section>

              <section className="triage-expand-card">
                {sectionId === "resolved" ? (
                  <div className="triage-resolution-banner">
                    <h4 className="triage-expand-card__title">
                      <FontAwesomeIcon icon={faCircleCheck} /> Resolution Summary
                    </h4>
                    <dl className="triage-kv">
                      <KV label="Status" value={<StatusPill workflowStatus="resolved" />} />
                      <KV label="Final Result" value={row.reviewResult || "Approved and Closed"} />
                      <KV
                        label="Resolved"
                        value={row.resolvedAt ? formatTriageDetected(row.resolvedAt) : null}
                      />
                      <KV label="Approved By" value={row.resolvedBy} />
                      <KV label="Outcome" value={row.outcome} />
                    </dl>
                  </div>
                ) : null}
                {hasDialogue ? (
                  <TriageReviewDialogue
                    row={row}
                    currentUser={currentUser}
                    readOnly={sectionId === "resolved"}
                    onSubmitMessage={onSubmitMessage}
                    onRequestClose={sectionId === "resolved" ? null : onRequestClose}
                    onRequestReopen={sectionId === "resolved" ? onRequestReopen : null}
                  />
                ) : (
                  <>
                    <h4 className="triage-expand-card__title">
                      <FontAwesomeIcon icon={faClipboardList} /> Workflow Status
                    </h4>
                    <dl className="triage-kv">
                      <KV label="Status" value={<StatusPill workflowStatus={row.workflowStatus} />} />
                      <KV label="Owner" value={row.owner} />
                      <KV label="Due" value={row.dueDate ? formatTriageDetected(row.dueDate) : null} />
                      <KV label="Priority" value={<PriorityTag priority={row.priority} />} />
                      <KV label="Action Type" value={row.actionType} />
                      <KV label="Notes" value={row.assignmentNotes || row.notes} />
                    </dl>
                    {showAssignOnly ? (
                      <button
                        type="button"
                        className="triage-btn-assign triage-btn-assign--block"
                        onClick={() => onAssign?.(row)}
                      >
                        Assign Follow-Up
                      </button>
                    ) : null}
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function SectionBand({ label, count, tone, sectionRef }) {
  return (
    <div
      role="row"
      className={`triage-section-band triage-section-band--${tone}`}
      ref={sectionRef}
    >
      <div role="cell" className="triage-section-band__label">
        {label} ({count})
      </div>
    </div>
  );
}

function QueueHeader({ sort, onSort, showReviewColumn, showResolvedColumns }) {
  const head = (className, label, sortKey) => (
    <div
      role="columnheader"
      className={`triage-grid-cell ${className}`}
      aria-sort={
        sortKey && sort?.key === sortKey
          ? sort.dir === "asc"
            ? "ascending"
            : "descending"
          : undefined
      }
    >
      <SortableHeader label={label} sortKey={sortKey} sort={sort} onSort={onSort} />
    </div>
  );
  return (
    <div className="triage-grid-row triage-grid-head" role="row">
      <div role="columnheader" className="triage-grid-cell triage-col-expand" aria-label="Expand" />
      {head("triage-col-client", "Client", "client")}
      {head("triage-col-score", "AIP Score", "score")}
      {head("triage-col-changed", "What Changed")}
      {head("triage-col-source", "Source")}
      {head("triage-col-detected", "Detected", "detected")}
      {head("triage-col-status", "Status")}
      {showReviewColumn
        ? head(
            "triage-col-review",
            showResolvedColumns ? "Final Result" : "Review Status",
          )
        : null}
      {head("triage-col-owner", "Owner", "owner")}
      {head("triage-col-due", "Due", "due")}
      {head("triage-col-priority", "Priority", "priority")}
      {head("triage-col-notes", "Notes")}
      {showResolvedColumns ? (
        <>
          {head("triage-col-resolved-at", "Resolved At", "resolvedAt")}
          {head("triage-col-resolved-by", "Resolved By")}
          {head("triage-col-outcome", "Outcome")}
        </>
      ) : null}
      {!showResolvedColumns ? head("triage-col-actions", "Action") : null}
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/** Compact page list: 1 … 4 5 6 … 12 */
function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages = new Set([0, total - 1, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
}

function SectionPager({ total, page, pageSize, onPage, onPageSize, tone }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize + 1;
  const end = Math.min(total, start + pageSize - 1);
  return (
    <div role="row" className={`triage-grid-row triage-pager-row triage-pager-row--${tone}`}>
      <div role="cell" className="triage-pager">
        <span className="triage-pager__summary">
          Showing {start}–{end} of {total}
        </span>
        <label className="triage-pager__size">
          Rows per page
          <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <nav className="triage-pager__pages" aria-label="Pagination">
          <button
            type="button"
            className="triage-pager__btn"
            onClick={() => onPage(page - 1)}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {pageNumbers(page, pageCount).map((p) =>
            typeof p === "string" ? (
              <span key={p} className="triage-pager__gap">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`triage-pager__btn${p === page ? " is-active" : ""}`}
                onClick={() => onPage(p)}
                aria-current={p === page ? "page" : undefined}
              >
                {p + 1}
              </button>
            ),
          )}
          <button
            type="button"
            className="triage-pager__btn"
            onClick={() => onPage(page + 1)}
            disabled={page >= pageCount - 1}
            aria-label="Next page"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </nav>
      </div>
    </div>
  );
}

const ASSIGNED_WORK_SECTIONS = [
  { id: "assigned", label: "Assigned / In Progress", tone: "blue" },
  { id: "overdue", label: "Overdue", tone: "amber" },
];

export default function TriageQueueTable({
  groups,
  queueView = "active",
  onViewClient,
  onAssignRow,
  onRowExpand,
  sort,
  onSort,
  layout = "triage",
  showBucketBadge = false,
  scrollToSection = null,
  onlySection = null,
  currentUser,
  onSubmitMessage,
  onRequestClose,
  onRequestReopen,
  onExploreInterventions,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [pages, setPages] = useState({});
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const sectionRefs = useRef({});

  const goToPage = (sectionId, page) => {
    setPages((prev) => ({ ...prev, [sectionId]: page }));
    setExpandedId(null);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const changePageSize = (size) => {
    setPageSize(size);
    setPages({});
  };

  useEffect(() => {
    if (!scrollToSection) return;
    const el = sectionRefs.current[scrollToSection];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollToSection]);

  const sections = (() => {
    if (queueView === "resolved") {
      return [{ id: "resolved", label: "Resolved / History", tone: "gray" }];
    }
    const base = layout === "assigned" ? ASSIGNED_WORK_SECTIONS : WORKFLOW_SECTIONS;
    return onlySection ? base.filter((s) => s.id === onlySection) : base;
  })();

  const showReviewColumn =
    layout === "assigned" ||
    sections.some((s) => ["assigned", "overdue", "resolved"].includes(s.id));
  const showResolvedColumns = queueView === "resolved";

  return (
    <div className="triage-table-wrap">
      <div
        className={`triage-queue-grid${showReviewColumn ? " triage-queue-grid--review" : ""}${showResolvedColumns ? " triage-queue-grid--resolved" : ""}`}
        role="table"
        aria-label="Triage cases"
      >
        <QueueHeader
          sort={sort}
          onSort={onSort}
          showReviewColumn={showReviewColumn}
          showResolvedColumns={showResolvedColumns}
        />

        {sections.map(({ id, label, tone }) => {
          const rows = groups[id] || [];
          const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
          // Clamp so a filter that shrinks the list never strands you on an empty page.
          const page = Math.min(pages[id] || 0, pageCount - 1);
          const pageRows = rows.slice(page * pageSize, (page + 1) * pageSize);
          return (
            <React.Fragment key={id}>
              <SectionBand
                label={label}
                count={rows.length}
                tone={tone}
                sectionRef={(node) => {
                  sectionRefs.current[id] = node;
                }}
              />
              {rows.length === 0 ? (
                <div
                  role="row"
                  className={`triage-grid-row triage-empty-row triage-empty-row--${tone}`}
                >
                  <div role="cell" className="triage-empty">
                    No cases in this section.
                  </div>
                </div>
              ) : (
                pageRows.map((row) => (
                  <TriageGridRow
                    key={row.id}
                    row={row}
                    sectionId={id}
                    expanded={expandedId === row.id}
                    onToggle={() => {
                      const next = expandedId === row.id ? null : row.id;
                      setExpandedId(next);
                      if (next) onRowExpand?.(row);
                    }}
                    onView={onViewClient}
                    onAssign={onAssignRow}
                    showBucketBadge={showBucketBadge}
                    showReviewColumn={showReviewColumn}
                    showResolvedColumns={showResolvedColumns}
                    currentUser={currentUser}
                    onSubmitMessage={onSubmitMessage}
                    onRequestClose={onRequestClose}
                    onRequestReopen={onRequestReopen}
                    onExploreInterventions={onExploreInterventions}
                  />
                ))
              )}
              {rows.length > PAGE_SIZE_OPTIONS[0] ? (
                <SectionPager
                  total={rows.length}
                  page={page}
                  pageSize={pageSize}
                  onPage={(p) => goToPage(id, p)}
                  onPageSize={changePageSize}
                  tone={tone}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
